import React, {useEffect, useRef, useState} from 'react';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {
  getRebatePaymentPageResult,
  initApplyPageContext,
  ViewEntityProps,
} from '@/pages/contract/RebatePaymentApply/components/data';
import {VatType} from '@/services/mdm/vattype';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {Col, Form, Input, message, Row} from 'antd';
import moment from 'moment';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {Invoices} from '@/services/sales/financial/invoices';
import DataStore from '@/components/GridWidget/DataStore';
import {PageType} from '@/services/basic/pagetype';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiSelect, WdiSelectFormat} from '@/components/WdiControls/WdiSelect';
import {RebatePaymentDetailsForFgApplyPanel} from '@/pages/contract/RebatePaymentApply/components/detail';
import FormInvoiceForApplyPanel from '@/pages/sales/sp/components/FormInvoice';
import {BankAccount} from '@/services/mdm/bankaccount';
import {Company} from '@/services/mdm/company';
import {Supplier} from '@/services/mdm/supplier';
import {OaContractSelectApp} from '@/pages/sales/sp/components/OaContractCmpt';
import {OaForm} from '@/services/mdm/oaform';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import SPWarningInfoApp from "@/pages/sales/sp/components/WarningInformation";
import {WarningInfoAppProps} from "@/components/WdiBus/PanelForWarning";


export const RebatePaymentTpApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [invoiceDataSource, setInvoiceDataSource] = useState<API.FormInvoiceVo[]>([]);
  const [vatType, setVatType] = useState<API.VatType>();
  const [editorRef,setEditorRef] = useState<any>();
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const onInvoiceChange = (data: any) => {
    setInvoiceDataSource(data);
  };


  const onVatTypeChange = (vatTypeId?: number) => {
    formRef.current?.setFieldsValue({
      vatTypeId: vatTypeId
    });
    setVatType(undefined);
    if (vatTypeId) {
      VatType.get(vatTypeId).then(responseResult => {
        setVatType(responseResult.data);
        if (dataSource && dataSource.length > 0) {
          const newDataSource = [...dataSource];
          newDataSource.forEach(item => {
            item.vatTypeValue = responseResult.data.vatTypeValue;
            if (!wdiCore.Object.IsNullOrUndefined(item.amountInVat) && !wdiCore.Object.IsNullOrUndefined(item.vatTypeValue)) {
              item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + item.vatTypeValue), 2);
              item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
            }
          })
          setDataSource(newDataSource);
        }
      })
    }
  }

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            invoiceStatus: viewEntity.formSp.invoiceStatus,
            payOffDate: viewEntity.formSp.payOffDate
              ? moment(viewEntity.formSp.payOffDate, 'YYYY-MM-DD')
              : undefined,
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.formSp.fileName,
              viewEntity.formSp.filePath,
            ),
          });
          Invoices.getForPayment(viewEntity.formSp.formSpId || 0).then((responseResult) => {
            let invoices = DataStore.init(responseResult.data);
            if (viewEntity.form?.relationType) {
              invoices = invoices.map(invoice => (Object.assign({}, invoice, {formInvoiceId: null})))
            }
            setInvoiceDataSource(invoices);
          });
          if (viewEntity.formSp.bankAccountId) {
            BankAccount.get(viewEntity.formSp.bankAccountId).then((res) => {
              formRef.current?.setFieldsValue({
                bankAccountId: {label: res.data.bankAccountNo, value: res.data.bankAccountId},
              });
            });
          }
          if (viewEntity.formSp && viewEntity.formSp.bankAccountName) {
            formRef.current?.setFieldsValue({
              bankAccountName: viewEntity.formSp.bankAccountName,
            });
          }
          if (viewEntity.formSp?.oaFormNo) {
            OaForm.get(viewEntity.formSp?.oaFormNo).then(res => {
              formRef.current?.setFieldsValue({
                oaFormNo: res.data,
              });
            })
          }
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
        }
        if (viewEntity.supplierId) {
          Supplier.get(viewEntity.supplierId).then((res) => {
            formRef.current?.setFieldsValue({
              supplier: res.data.codeOrName,
              supplierId: res.data.supplierId,
            });
          });
        }
        onVatTypeChange(viewEntity.vatTypeId);

        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }
        if (viewEntity.companyCode) {
          Company.get(viewEntity.companyCode).then((res) => {
            formRef.current?.setFieldsValue({
              companyCode: res.data.companyCode,
              company: res.data.codeOrName,
            });
          });
        }
        if (viewEntity.formSpDetails) {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "返利核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        initialValues={{ invoiceStatus: Enum.InvoiceStatus.WITH_INVOICE }}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/contract/myrebateinfo"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getRebatePaymentPageResult(
                    values,
                    entity,
                    pageType,
                    statusId,
                    dataSource,
                    invoiceDataSource,
                  );
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle title="返利核销信息" />}>
          <Row gutter={[16, 0]}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="vatTypeId"
                label="可抵扣税率类型(普票为0)"
                rules={[{ required: true, message: '可抵扣税率类型不能为空' }]}
              >
                <WdiSelect
                  {...{
                    placeholder: '请选择可抵扣税率类型',
                    onChange: (value) => {
                      onVatTypeChange(value);
                    },
                    dataLoading: async (params) => {
                      const response = await VatType.search(
                        Object.assign({ isActive: true }, params),
                      );
                      return response.data.map((item) => {
                        return { value: item.vatTypeId, label: item.vatTypeName };
                      });
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="bankAccountId" label="银行账号" rules={[{ required: true }]}>
                <WdiSelect
                  {...{
                    placeholder: '请选择银行账号',
                    reloadKey: entity?.companyCode,
                    labelInValue: true,
                    dataLoading: async (params) => {
                      const response = await BankAccount.search(
                        Object.assign(
                          {
                            companyCode: entity?.companyCode,
                            supplierId: entity?.supplierId,
                            isActive: true,
                          },
                          params,
                        ),
                      );
                      return response.data.map((item) => {
                        return {
                          value: item.bankAccountId,
                          label: item.bankAccountNo,
                        };
                      });
                    },
                    onChange: (value) => {
                      BankAccount.get(WdiSelectFormat.getvalue(value)).then((res) => {
                        formRef.current?.setFieldsValue({
                          bankAccountName: res.data.bankAccountName,
                        });
                      });
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="bankAccountName" label="户名" rules={[{ required: true }]}>
                <WdiInput />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="oaFormNo"
                label="OA合同号"
              >
                <OaContractSelectApp />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col className="gutter-row" hidden={true}>
              <Form.Item name="companyCode" label="公司">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="company" label="公司">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col className="gutter-row" hidden={true}>
              <Form.Item name="supplierId" label="供应商">
                <WdiInput readOnly />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="supplier" label="供应商">
                <WdiInput readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="核销备注">
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <RebatePaymentDetailsForFgApplyPanel
          data={{
            paymentMethodId: entity?.paymentMethodId,
            vatType: vatType,
            pageTypeId: entity?.pageTypeId,
            supplierId: entity?.supplierId,
            companyCode: entity?.companyCode,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />

        <FormInvoiceForApplyPanel
          data={{
            vatType: vatType,
            companyCode: entity?.companyCode,
            details: dataSource,
            supplierId: entity?.supplierId,
            paymentMethodId: entity?.paymentMethodId,
          }}
          dataSource={invoiceDataSource}
          onChange={onInvoiceChange}
        />
      </ProForm>
    </>
  );
};
